import axios from "axios";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL || "https://api.pickdengu.mw";

const api = axios.create({
  baseURL: `${API_BASE}/api/v1/finance`,
  headers: {
    "Content-Type": "application/json",
  },
});

export type PayoutType = "seller" | "driver";

export type PayoutDestinationPayload = {
  ownerType: PayoutType;
  ownerId: string;
  method: "bank" | "airtel_money" | "tnm_mpamba";
  displayName?: string;
  accountName: string;
  bank?: {
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
    swiftCode?: string;
  };
  mobileMoney?: {
    phoneNumber?: string;
  };
  isDefault?: boolean;
  createdBy?: string;
};

export type CreatePayoutBatchPayload = {
  payoutType: PayoutType;
  periodType: "weekly" | "monthly";
  createdBy?: string;
};

export type CancelPayoutBatchPayload = {
  cancelledBy?: string;
  reason: string;
};

export type MarkPayoutBatchPaidPayload = {
  transferReference: string;
};

export type MarkPayoutItemPaidPayload = {
  payoutType: PayoutType;
  transferReference: string;
};

export type PayConsolidatedPayoutPayload = {
  payoutType: PayoutType;
  ownerId: string;
  periodType?: "weekly" | "monthly";
  transferReference: string;
};

export type MarkPayoutItemFailedPayload = {
  failureReason: string;
};

export type RetryPayoutItemPayload = {
  requestedBy?: string;
};

export type VerifyPayoutDestinationPayload = {
  verificationStatus: "verified" | "failed";
  verificationNote?: string;
  verifiedBy?: string;
};

type QueryParams = Record<string, string | number | boolean | undefined>;

export const financeApi = {
  getOverview: async () => {
    const { data } = await api.get("/admin/overview");
    return data;
  },

  getAdminPayments: async (params?: QueryParams) => {
    const { data } = await api.get("/admin/payments", { params });
    return data;
  },

  getAdminSales: async (params?: QueryParams) => {
    const { data } = await api.get("/admin/sales", { params });
    return data;
  },

  getAdminDriverEarnings: async (params?: QueryParams) => {
    const { data } = await api.get("/admin/driver-earnings", { params });
    return data;
  },

  getSellerFinance: async (sellerUserId: string) => {
    const { data } = await api.get(`/seller/${sellerUserId}`);
    return data;
  },

  getDriverFinance: async (driverId: string) => {
    const { data } = await api.get(`/driver/${driverId}`);
    return data;
  },

  getPayoutBatches: async () => {
    const { data } = await api.get("/payouts/batches");
    return data;
  },

  getPayoutBatchById: async (batchId: string) => {
    const { data } = await api.get(`/payouts/batches/${batchId}`);
    return data;
  },

  createPayoutBatch: async (payload: CreatePayoutBatchPayload) => {
    const { data } = await api.post("/payouts/batches", payload);
    return data;
  },

  markPayoutBatchPaid: async (
    batchId: string,
    payload: MarkPayoutBatchPaidPayload,
  ) => {
    const { data } = await api.post(`/payouts/batches/${batchId}/pay`, payload);
    return data;
  },

  getConsolidatedOpenPayouts: async (params?: QueryParams) => {
    const { data } = await api.get("/payouts/consolidated/open", { params });
    return data;
  },

  payConsolidatedPayout: async (payload: PayConsolidatedPayoutPayload) => {
    const { data } = await api.post("/payouts/consolidated/pay", payload);
    return data;
  },

  cancelPayoutBatch: async (
    batchId: string,
    payload: CancelPayoutBatchPayload,
  ) => {
    const { data } = await api.patch(
      `/payouts/batches/${batchId}/cancel`,
      payload,
    );
    return data;
  },

  markPayoutItemPaid: async (
    payoutItemId: string,
    payload: MarkPayoutItemPaidPayload,
  ) => {
    const { data } = await api.post(
      `/payouts/items/${payoutItemId}/pay`,
      payload,
    );
    return data;
  },

  markPayoutItemFailed: async (
    payoutItemId: string,
    payload: MarkPayoutItemFailedPayload,
  ) => {
    const { data } = await api.patch(
      `/payouts/items/${payoutItemId}/fail`,
      payload,
    );
    return data;
  },

  retryPayoutItem: async (
    payoutItemId: string,
    payload: RetryPayoutItemPayload = {},
  ) => {
    const { data } = await api.patch(
      `/payouts/items/${payoutItemId}/retry`,
      payload,
    );
    return data;
  },

  getPayoutDestinations: async (params?: QueryParams) => {
    const { data } = await api.get("/admin/payout-destinations", { params });
    return data;
  },

  createPayoutDestination: async (payload: PayoutDestinationPayload) => {
    const { data } = await api.post("/admin/payout-destinations", payload);
    return data;
  },

  verifyPayoutDestination: async (
    destinationId: string,
    payload: VerifyPayoutDestinationPayload,
  ) => {
    const { data } = await api.patch(
      `/admin/payout-destinations/${destinationId}/verify`,
      payload,
    );
    return data;
  },

  setDefaultPayoutDestination: async (destinationId: string) => {
    const { data } = await api.patch(
      `/admin/payout-destinations/${destinationId}/default`,
    );
    return data;
  },

  getLedgerEntries: async (params?: QueryParams) => {
    const { data } = await api.get("/admin/ledger", { params });
    return data;
  },
};

export default financeApi;
